import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOrderHistory } from '@/contexts/OrderHistoryContext';
import { usePriceFormat } from '@/hooks/usePriceFormat';
import { useAuth } from '@/contexts/AuthContext';
import { apiService } from '@/services/api';
import { EventOut } from '@/types/api';
import Card from '@/components/atoms/Card';
import Button from '@/components/atoms/Button';
import Icon from '@/components/atoms/Icon/Icon';
import { useDateFormatter } from '@/hooks/useDateFormatter';
import { parseServerDate } from '@/utils/dateUtils';
import './OrderHistory.scss';
import { Input } from '@/components/atoms/Input';

const OrderHistory: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { orders, clearOrderHistory, hasOrders, updateOrderFromDatabase, syncOrdersFromDatabase, isSyncing } = useOrderHistory();
  const { formatPrice, formatWithTax } = usePriceFormat();
  const { formatOrderDate } = useDateFormatter();
  const { isAuthenticated, user } = useAuth();
  const [events, setEvents] = useState<EventOut[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [expandedOrderId, setExpandedOrderId] = useState<number | null>(null);
  const [eventsLoaded, setEventsLoaded] = useState(false);

  // Fetch events only once when component mounts or auth changes
  useEffect(() => {
    const fetchEvents = async () => {
      if (eventsLoaded) return; // Avoid refetching if already loaded
      
      try {
        // Use appropriate endpoint based on authentication status
        const eventsData = isAuthenticated 
          ? await apiService.getEvents()
          : await apiService.getAnonymousEvents();
        setEvents(eventsData);
        setEventsLoaded(true);
      } catch (error) {
        console.error('Error fetching events for order history:', error);
      }
    };

    if (hasOrders) {
      fetchEvents();
    }
  }, [hasOrders, isAuthenticated, eventsLoaded]);

  // Sync orders only once when component mounts or when auth changes
  useEffect(() => {
    const syncOrders = async () => {
      try {
        await syncOrdersFromDatabase();
      } catch (error) {
        console.error('Error syncing orders from database:', error);
      }
    };

    if (hasOrders) {
      syncOrders();
    }
  }, [hasOrders, isAuthenticated]);

  const getEventName = (order: any) => {
    const event_id = order.event_id;
    if (events.length > 0) {
      const eventName = events.find(event => event.id === event_id)?.name;
      return eventName ?? 'Event';
    }
    return 'Event';
  };

  // Helper function to format date for display
  const formatOrderDateTime = (dateString: string) => {
    const date = parseServerDate(dateString);
    return {
      date: date.toLocaleDateString('es-ES', { 
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }),
      time: date.toLocaleTimeString('es-ES', { 
        hour: '2-digit', 
        minute: '2-digit' 
      })
    };
  };

  // Security: Filter orders by ownership first (defense in depth)
  const ownedOrders = React.useMemo(() => {
    if (isAuthenticated && user) {
      // For authenticated users: only show orders where customer_id matches user.id
      return orders.filter(order => order.customer_id === user.id);
    }
    // For anonymous users: show orders from context (which should only be orders they created this session)
    // The context already filters out orders from sessionStorage for anonymous users
    return orders;
  }, [orders, isAuthenticated, user]);

  // Filter orders based on search term and date
  const filteredOrders = ownedOrders.filter(order => {
    const matchesSearch = searchTerm === '' || 
      order.id.toString().includes(searchTerm.toLowerCase()) ||
      getEventName(order).toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.items.some(item => 
        (item.drink_name || item.food_name || '').toLowerCase().includes(searchTerm.toLowerCase())
      );

    const matchesDate = filterDate === '' || (() => {
      if (!filterDate) return true;
      
      const orderDate = parseServerDate(order.created_at);
      const filterDateObj = new Date(filterDate + 'T00:00:00'); // Ensure timezone consistency
      
      // Set filter date to start of day (00:00:00)
      const filterStart = new Date(filterDateObj);
      filterStart.setHours(0, 0, 0, 0);
      
      // Set filter date to end of day (23:59:59)
      const filterEnd = new Date(filterDateObj);
      filterEnd.setHours(23, 59, 59, 999);
      
      // Check if order date is between start and end of the selected date
      const isInRange = orderDate >= filterStart && orderDate <= filterEnd;
      
      // Debug logging (remove in production)
      if (process.env.NODE_ENV === 'development') {
        console.log('Date filter debug:', {
          orderId: order.id,
          orderDate: orderDate.toISOString(),
          filterDate: filterDate,
          filterStart: filterStart.toISOString(),
          filterEnd: filterEnd.toISOString(),
          isInRange
        });
      }
      
      return isInRange;
    })();

    return matchesSearch && matchesDate;
  });

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'warning';
      case 'confirmed':
      case 'preparing':
        return 'info';
      case 'ready':
      case 'completed':
      case 'paid':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleViewOrder = async (orderId: number) => {
    // Only sync if not already syncing to avoid multiple requests
    if (!isSyncing) {
      try {
        await updateOrderFromDatabase(orderId);
      } catch (error) {
        console.error('Error updating order from database:', error);
      }
    }
    
    setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
  };

  const handleClearHistory = () => {
    if (window.confirm(t('orderHistory.confirmClear'))) {
      clearOrderHistory();
    }
  };

  if (!hasOrders) {
    return (
      <div className="container">
        <div className="order-history-page">
          <div className="order-history-page__header">
            <h1 className="order-history-page__title">
              {t('orderHistory.title')}
            </h1>
          </div>

          <Card variant="outlined" className="empty-history-card">
            <div className="empty-history-card__content">
              <div className="empty-history-card__icon">
                <Icon name="pedidos" size={40} alt={t('orderHistory.empty')} />
              </div>
              <h2 className="empty-history-card__title">
                {t('orderHistory.empty')}
              </h2>
              <p className="empty-history-card__description">
                {t('orderHistory.emptyDescription')}
              </p>
              <Button 
                variant="primary" 
                size="lg"
                onClick={() => navigate('/events')}
              >
                {t('orderHistory.browseEvents')}
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="order-history-page">
        <div className="order-history-page__header">
          <h1 className="order-history-page__title">
            {t('orderHistory.title')}
          </h1>
          
        </div>

        <div className="order-history-page__search-filter">
          <div className="order-history-page__search-container">
            <Input
              type="text"
              placeholder={t('orderHistory.searchFilter.placeholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="order-history-page__search-input"
            />
            <Icon className="order-history-page__search-icon" name="lupa" size={20} alt={t('orderHistory.searchFilter.placeholder')} />
          </div>
        </div>

        <div className="order-history-page__content">
          {filteredOrders.map((order) => {
            return (
              <Card key={order.id} variant="elevated" className="order-card">
                <div className="order-card__header">
                  <div className="order-card__number">
                    #{ order.id }
                  </div>
                  <div className="order-card__time">
                    {(() => {
                      const { date, time } = formatOrderDateTime(order.created_at);
                      return `${date} ${time}`;
                    })()}
                  </div>
                </div>

                <div className="order-card__establishment">
                  {getEventName(order)}
                </div>

                <div className="order-card__items">
                  <ul className="order-card__items-list">
                    {order.items.slice(0, 3).map((item, index) => (
                      <li key={index} className="order-item">
                        {item.quantity} {item.drink_name || item.food_name}
                      </li>
                    ))}
                    {order.items.length > 3 && (
                      <li className="order-item order-item--more">
                        {t('orderHistory.andMore', { count: order.items.length - 3 })}
                      </li>
                    )}
                  </ul>
                  <div className="order-card__status">
                    <span className={`status status--${getStatusColor(order.status)}`}>
                      {t(`orderConfirmation.statuses.${order.status.toLowerCase()}`)}
                    </span>
                    <Button 
                      variant="outline" 
                      size="xs"
                      onClick={async () => {
                        try {
                          await syncOrdersFromDatabase(true); // Force sync on manual refresh
                        } catch (error) {
                          console.error('Error syncing orders:', error);
                        }
                      }}
                      disabled={isSyncing}
                      className="order-history-page__sync-button"
                    >
                      <Icon name="actualizar" size={16} alt={isSyncing ? t('common.loading') : t('common.refresh')} /> 
                    </Button>
                  </div>
                </div>
                {expandedOrderId !== order.id && (
                <div className="order-card__actions">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewOrder(order.id)}
                    className="order-card__view-details"
                  >
                      <>
                        <Icon name="abajo" size={16} alt="Show" />
                        {t('orderHistory.viewDetails')}
                      </>
                  </Button>
                </div>
                )}

                {/* Expanded Details */}
                {expandedOrderId === order.id && (
                  <>
                  <div className="order-card__details">
                    <div className="order-details">
                      {/* Status-specific message */}
                      <div className="order-details__status-message">
                        <h3 className="order-details__status-title">
                          {t(`orderHistory.statusMessages.${order.status.toLowerCase()}`,
                            t('orderHistory.statusMessages.paid')
                          )}
                        </h3>
                      </div>

                      {/* Items List */}
                      <div className="order-details__section">
                        <h3 className="order-details__title">{t('orderHistory.items')}</h3>
                        <ul className="order-details__items-list">
                          {order.items.map((item, index) => (
                            <li key={index} className="order-details__item">
                              <div className="order-details__item-info">
                                <span className="order-details__item-quantity">{item.quantity}x</span>
                                <span className="order-details__item-name">
                                  {item.drink_name || item.food_name}
                                </span>
                                {(item.country_tax_rate !== null && item.country_tax_rate !== undefined) && (
                                  <div className="order-details__item-tax-breakdown">
                                    <span className="order-details__tax-info">
                                      {t('order.itemTaxRate')}: {item.tax_rate_at_time.toFixed(2)}%
                                    </span>
                                    <span className="order-details__tax-info">
                                      {t('order.countryTaxRate')}: {item.country_tax_rate.toFixed(2)}%
                                    </span>
                                    {item.combined_tax_rate !== null && item.combined_tax_rate !== undefined && (
                                      <span className="order-details__tax-info order-details__tax-info--combined">
                                        {t('order.combinedTaxRate')}: {item.combined_tax_rate.toFixed(2)}%
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                              <span className="order-details__item-price">
                                {formatWithTax(
                                  item.price_at_time * item.quantity,
                                  item.combined_tax_rate !== null && item.combined_tax_rate !== undefined
                                    ? item.combined_tax_rate
                                    : item.tax_rate_at_time
                                )}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Payment Summary - shown for completed orders */}
                      {(order.status.toLowerCase() === 'completed' ||
                        order.status.toLowerCase() === 'delivered') && (
                          <div className="order-details__section">
                            <div className="order-details__payment-info">
                              <div className="order-details__payment-row">
                                <span className="order-details__payment-label">{t('orderHistory.amountPaid')}:</span>
                                <span className="order-details__payment-amount">{formatPrice(order.total_amount)}</span>
                              </div>
                              <div className="order-details__payment-row">
                                <span className="order-details__payment-label">{t('orderHistory.paymentMethod')}:</span>
                                <span className="order-details__payment-method">
                                  <Icon name="tarjeta-credito" size={16} alt="Card" />
                                  Tarjeta de Crédito
                                </span>
                              </div>
                            </div>
                          </div>
                        )}

                      {/* Total - shown for non-completed orders */}
                      {order.status.toLowerCase() !== 'completed' &&
                        order.status.toLowerCase() !== 'delivered' && (
                          <div className="order-details__section">
                            <div className="order-details__summary">
                              <div className="order-details__row">
                                <span className="order-details__label">{t('common.total')}</span>
                                <span className="order-details__total">
                                  {formatPrice(order.total_amount)}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}

                      {/* Order Info */}
                      <div className="order-details__section">
                        <div className="order-details__info-grid">
                          <div className="order-details__info-item">
                            <span className="order-details__info-label">
                              {t('orderConfirmation.status')}:
                            </span>
                            <span className={`status status--${getStatusColor(order.status)}`}>
                              {t(`orderConfirmation.statuses.${order.status.toLowerCase()}`)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                    <div className="order-card__actions-footer">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewOrder(order.id)}
                      className="order-card__view-details"
                      fullWidth
                    >
                      <Icon name="arriba" size={16} alt="Show" />
                      {t('orderHistory.hideDetails').toLowerCase()}
                    </Button>
                  </div>
                  </>
                )}
              </Card>
            );
          })}
        </div>

        <div className="order-history-page__footer">
          <p className="order-history-page__subtitle">
            {t('orderHistory.subtitle', { count: orders.length })}
          </p>
          <div className="order-history-page__actions">
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleClearHistory}
            >
              {t('orderHistory.clearHistory')}
            </Button>
          </div>
          <Button 
            variant="outline" 
            size="lg"
            onClick={() => navigate('/events')}
          >
            {t('orderHistory.orderMore')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OrderHistory;